import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, PartyPopper, Rocket, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

// Patriotic sound generator using Web Audio API
const playPatrioticFanfare = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Create a triumphant patriotic melody
    const notes = [
      { freq: 523.25, start: 0, duration: 0.3 },    // C5
      { freq: 659.25, start: 0.3, duration: 0.3 },  // E5
      { freq: 783.99, start: 0.6, duration: 0.4 },  // G5
      { freq: 1046.50, start: 1.0, duration: 0.6 }, // C6 (climax)
      { freq: 783.99, start: 1.6, duration: 0.2 },  // G5
      { freq: 1046.50, start: 1.8, duration: 0.8 }, // C6 (ending)
    ];

    notes.forEach(note => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.type = "triangle";
      oscillator.frequency.value = note.freq;
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime + note.start);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + note.start + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + note.start + note.duration);
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.start(audioContext.currentTime + note.start);
      oscillator.stop(audioContext.currentTime + note.start + note.duration);
    });

    // Add drum beats
    [0, 0.5, 1.0, 1.5, 2.0].forEach(time => {
      const noise = audioContext.createOscillator();
      const noiseGain = audioContext.createGain();
      noise.type = "square";
      noise.frequency.value = 80;
      noiseGain.gain.setValueAtTime(0, audioContext.currentTime + time);
      noiseGain.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + time + 0.02);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + time + 0.15);
      noise.connect(noiseGain);
      noiseGain.connect(audioContext.destination);
      noise.start(audioContext.currentTime + time);
      noise.stop(audioContext.currentTime + time + 0.15);
    });
  } catch (e) {
    console.log("Audio not supported");
  }
};

// Confetti particle component
const ConfettiParticle = ({ delay, color, startX }: { delay: number; color: string; startX: number }) => (
  <motion.div
    className="absolute w-2 h-3 rounded-sm"
    style={{ backgroundColor: color, left: `${startX}%` }}
    initial={{ y: -20, x: 0, rotate: 0, opacity: 1 }}
    animate={{
      y: 200,
      x: [0, Math.random() * 40 - 20, Math.random() * 60 - 30],
      rotate: [0, 360, 720, 1080],
      opacity: [1, 1, 0],
    }}
    transition={{
      duration: 3,
      delay,
      repeat: Infinity,
      repeatDelay: 2,
      ease: "easeOut",
    }}
  />
);

// Firework burst component
const FireworkBurst = ({ x, delay }: { x: number; delay: number }) => {
  const colors = ["#FF9933", "#FFFFFF", "#138808", "#000080"];
  const particles = Array.from({ length: 12 }, (_, i) => ({
    angle: (i * 30) * (Math.PI / 180),
    color: colors[i % 4],
  }));

  return (
    <motion.div
      className="absolute"
      style={{ left: `${x}%`, top: "50%" }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: [0, 1, 1.2, 0], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 1.5, delay, repeat: Infinity, repeatDelay: 4 }}
    >
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className="absolute w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: p.color }}
          initial={{ x: 0, y: 0 }}
          animate={{
            x: Math.cos(p.angle) * 30,
            y: Math.sin(p.angle) * 30,
            opacity: [1, 0],
          }}
          transition={{ duration: 0.8, delay: delay + 0.2, repeat: Infinity, repeatDelay: 4.5 }}
        />
      ))}
    </motion.div>
  );
};

// Sparkle component
const Sparkle = ({ x, y, delay }: { x: number; y: number; delay: number }) => (
  <motion.div
    className="absolute"
    style={{ left: `${x}%`, top: `${y}%` }}
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
    transition={{ duration: 0.6, delay, repeat: Infinity, repeatDelay: 1.5 }}
  >
    <svg width="16" height="16" viewBox="0 0 16 16">
      <path
        d="M8 0L9 6L16 8L9 10L8 16L7 10L0 8L7 6Z"
        fill="#FFD700"
      />
    </svg>
  </motion.div>
);

export const RepublicDayBanner = () => {
  const { user } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const hasPlayedSound = useRef(false);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem("republic_day_banner_dismissed") === "true";
  });

  useEffect(() => {
    const now = new Date();
    const isJanuary = now.getMonth() === 0;
    const isWithinDateRange = now.getDate() >= 20 && now.getDate() <= 31;
    const isSpecificUser = user?.email?.toLowerCase() === "businesswithswarit1@gmail.com";
    
    // For now, only show to specific user OR during Jan 20-31 for that user
    // Remove isSpecificUser check after testing to show for all users during the date range
    if (isSpecificUser) {
      setIsVisible(!dismissed);
    } else {
      // For other users, only show during Jan 20-31
      setIsVisible(isJanuary && isWithinDateRange && !dismissed && isSpecificUser);
    }
  }, [dismissed, user]);

  const handlePlaySound = useCallback(() => {
    setSoundEnabled(prev => !prev);
    if (!hasPlayedSound.current) {
      playPatrioticFanfare();
      hasPlayedSound.current = true;
    } else if (!soundEnabled) {
      playPatrioticFanfare();
    }
  }, [soundEnabled]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("republic_day_banner_dismissed", "true");
  };

  if (!isVisible) return null;

  // Tricolor confetti colors
  const confettiColors = ["#FF9933", "#FFFFFF", "#138808", "#000080"];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="fixed top-16 left-0 right-0 z-40 px-4 py-2"
      >
        <div className="max-w-4xl mx-auto relative overflow-hidden rounded-2xl">
          {/* Tricolor Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-white to-green-600 opacity-90" />
          
          {/* Animated shimmer overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
          />
          
          {/* Pattern overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.2),transparent_50%)]" />
          
          {/* Confetti particles */}
          {confettiColors.flatMap((color, colorIndex) =>
            Array.from({ length: 4 }, (_, i) => (
              <ConfettiParticle
                key={`${colorIndex}-${i}`}
                color={color}
                delay={i * 0.3 + colorIndex * 0.15}
                startX={5 + (colorIndex * 25) + (i * 5)}
              />
            ))
          )}

          {/* Firework bursts */}
          <FireworkBurst x={15} delay={0} />
          <FireworkBurst x={50} delay={1.5} />
          <FireworkBurst x={85} delay={3} />

          {/* Sparkles */}
          <Sparkle x={10} y={20} delay={0} />
          <Sparkle x={30} y={70} delay={0.5} />
          <Sparkle x={70} y={30} delay={1} />
          <Sparkle x={90} y={60} delay={1.5} />
          
          {/* Ashoka Chakra decoration - enhanced */}
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            className="absolute right-8 top-1/2 -translate-y-1/2 w-16 h-16"
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-lg">
                <circle cx="50" cy="50" r="45" fill="none" stroke="#000080" strokeWidth="3" />
                <circle cx="50" cy="50" r="8" fill="#000080" />
                {[...Array(24)].map((_, i) => (
                  <line
                    key={i}
                    x1="50"
                    y1="15"
                    x2="50"
                    y2="42"
                    stroke="#000080"
                    strokeWidth="2"
                    transform={`rotate(${i * 15} 50 50)`}
                  />
                ))}
              </svg>
            </motion.div>
          </motion.div>
          
          <div className="relative px-4 py-3 md:px-6 md:py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, -10, 10, 0]
                }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <PartyPopper className="h-6 w-6 text-orange-600 drop-shadow-md" />
              </motion.div>
              
              <div className="text-center flex-1">
                <h3 className="font-bold text-gray-900 text-sm md:text-base flex items-center gap-2 justify-center">
                  <motion.span 
                    className="hidden md:inline"
                    animate={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                  >
                    🇮🇳
                  </motion.span>
                  <motion.span
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  >
                    Happy Republic Day!
                  </motion.span>
                  <motion.span 
                    className="hidden md:inline"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                  >
                    🇮🇳
                  </motion.span>
                </h3>
                <p className="text-xs md:text-sm text-gray-700 flex items-center gap-1 justify-center">
                  <motion.div
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                  >
                    <Rocket className="h-3 w-3" />
                  </motion.div>
                  <span className="font-medium">KAIRAUX launching 26th January!</span>
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Sparkles className="h-3 w-3" />
                  </motion.div>
                </p>
              </div>
              
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="h-6 w-6 text-green-700 drop-shadow-md" />
              </motion.div>
            </div>
            
            <div className="flex items-center gap-1">
              {/* Sound toggle button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePlaySound}
                className="shrink-0 h-8 w-8 text-gray-700 hover:bg-white/30"
                title={soundEnabled ? "Mute" : "Play patriotic sound"}
              >
                {soundEnabled ? (
                  <Volume2 className="h-4 w-4" />
                ) : (
                  <VolumeX className="h-4 w-4" />
                )}
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDismiss}
                className="shrink-0 h-8 w-8 text-gray-700 hover:bg-white/30"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Bottom wave animation */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-white to-green-600"
            animate={{ 
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
            }}
            transition={{ duration: 3, repeat: Infinity }}
            style={{ backgroundSize: "200% 200%" }}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
